import { fetchAuthSession } from "aws-amplify/auth";
import { authReady } from "./auth";

/**
 * Portal fetch wrapper — Loop 39.
 *
 * Responsibilities:
 *   1. Attach the current Cognito ID token as Authorization: Bearer <jwt>.
 *   2. On 401, redirect the browser to /portal/login?next=<currentPath> so
 *      deep links survive token expiry. The caller still receives a
 *      rejected promise so UI layers can stop local spinners.
 *   3. Offer an optional `idempotencyKey` option that adds the
 *      `Idempotency-Key` header for safe retries (Loop 38 infra).
 *
 * This is the single fetch entry point for anything under /admin/* or any
 * other authenticated API route. Never call bare `fetch` from portal
 * pages — the 401 handling and auth wiring live here.
 */

// Match the default used by tenant.ts + checkout.ts. An empty default
// resolves API calls relative to the SPA origin, which CloudFront
// happily serves the SPA fallback for — so the caller gets an HTML
// body and a parse error, not a 404. Surfaced during Phase 14 Loop 4
// when the notifications poll hit /v1/admin/notifications on the
// marketing origin and got index.html back.
const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? "https://api.nexirt.com";

export type ApiFetchOptions = Omit<RequestInit, "headers"> & {
  headers?: Record<string, string>;
  idempotencyKey?: string;
  /** Skip the auth header (public endpoints like POST /contact). */
  anonymous?: boolean;
  /**
   * When true, a 401 response does NOT trigger the /portal/login redirect.
   * The caller receives the raw Response so it can inspect the body (e.g.
   * to detect "fresh MFA required" before deciding how to handle it).
   * The promise resolves (not rejects) with the 401 Response in this case.
   */
  skipRedirectOn401?: boolean;
};

export class ApiError extends Error {
  constructor(public status: number, public body: string) {
    super(`API ${status}`);
  }
}

/**
 * Extract the human-readable error from an ApiError response body,
 * falling back to the generic `API <status>` if the body isn't a
 * JSON `{ error: string }` envelope. Use this in admin toasts so
 * operators see the real backend reason ("Mail send failed: Bad
 * RequestException", "Username exists", etc.) instead of "API 400".
 */
export function describeApiError(err: unknown): string {
  if (!(err instanceof ApiError)) {
    return err instanceof Error ? err.message : "Unknown error";
  }
  try {
    const parsed = JSON.parse(err.body) as { error?: string };
    if (parsed?.error && typeof parsed.error === "string") return parsed.error;
  } catch {
    /* fall through */
  }
  return err.message;
}

function redirectToLogin() {
  if (typeof window === "undefined") return;
  const here = window.location.pathname + window.location.search;
  const next = encodeURIComponent(here);
  // Avoid loops — don't redirect if we're already on the login page.
  if (window.location.pathname.startsWith("/portal/login")) return;
  window.location.assign(`/portal/login?next=${next}`);
}

function randomKey(): string {
  // Non-crypto randomness is fine: the key's only job is to dedupe retries
  // from the same client. Prefix with time to keep it sortable.
  const rand = Math.random().toString(36).slice(2, 10);
  return `${Date.now().toString(36)}-${rand}`;
}

async function authHeader(): Promise<Record<string, string>> {
  if (!authReady) return {};
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    return token ? { authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

export async function apiFetch(path: string, opts: ApiFetchOptions = {}): Promise<Response> {
  const url = path.startsWith("http") ? path : `${API_BASE}${path}`;
  const headers: Record<string, string> = {
    "content-type": "application/json",
    ...(opts.anonymous ? {} : await authHeader()),
    ...(opts.idempotencyKey ? { "idempotency-key": opts.idempotencyKey } : {}),
    ...(opts.headers ?? {}),
  };
  const res = await fetch(url, { ...opts, headers });
  if (res.status === 401 && !opts.anonymous && !opts.skipRedirectOn401) {
    redirectToLogin();
    throw new ApiError(401, await res.text().catch(() => ""));
  }
  return res;
}

export async function apiJson<T = unknown>(path: string, opts: ApiFetchOptions = {}): Promise<T> {
  const method = (opts.method ?? "GET").toUpperCase();
  const isMutating = method !== "GET" && method !== "OPTIONS";
  const key = isMutating && !opts.idempotencyKey && !opts.anonymous ? randomKey() : opts.idempotencyKey;
  const res = await apiFetch(path, { ...opts, idempotencyKey: key });
  const text = await res.text();
  if (!res.ok) throw new ApiError(res.status, text);
  return (text ? JSON.parse(text) : null) as T;
}
